import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Stack,
  Container,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import api, { generateUserPDF } from "../services/api";
import { AVAILABLE_COURSES } from "../constants/courses";

// Get the user API base URL
const USER_BASE_URL =
  process.env.REACT_APP_USER_API_URL ||
  "https://backend.hunarmandpunjab.org.pk";

interface User {
  _id: string;
  rollNumber: string;
  email: string;
  fullName: string;
  fatherName: string;
  cnic: string;
  mobile: string;
  dateOfBirth: string;
  gender: "male" | "female";
  qualification: string;
  courses: string[];
  permanentAddress: string;
  city: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  scholarship?: {
    _id: string;
    fullName: string;
    cnic: string;
    rollNumber: string;
    email: string;
    mobileNumber: string;
    challanNumber: string;
    imagePath?: string;
    status: "pending" | "approved" | "rejected";
    appliedAt: string;
    createdAt: string;
    updatedAt: string;
  } | null;
}

interface Challan {
  _id: string;
  userId: {
    _id: string;
    fullName: string;
    email: string;
    rollNumber: string;
  } | null;
  challanId: string;
  amount: number;
  paid: boolean;
  path?: string;
  branchCode?: number | null;
  txnDate?: string | null;
  txnId?: number | null;
  createdAt: string;
  updatedAt: string;
}

interface AuditLog {
  _id: string;
  entityType: "user" | "challan";
  entityId: string;
  action: string;
  changes: Record<string, { old: any; new: any }>;
  adminUser: string;
  timestamp: string;
  reason?: string;
}

function EditRecords() {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedChallan, setSelectedChallan] = useState<Challan | null>(null);
  const [editedUser, setEditedUser] = useState<User | null>(null);
  const [editedChallan, setEditedChallan] = useState<Challan | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [challans, setChallans] = useState<Challan[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Modal states
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [auditLogModalOpen, setAuditLogModalOpen] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [changeReason, setChangeReason] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [adminApiKey, setAdminApiKey] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pendingAction, setPendingAction] = useState<"user" | "challan" | null>(
    null
  );

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when tab changes
    if (activeTab === 0) {
      fetchUsers();
    } else {
      fetchChallans();
    }
  }, [activeTab]);

  // Debounced search effect - reset page when search changes
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      if (activeTab === 0) {
        fetchUsers();
      } else {
        fetchChallans();
      }
    }, 300); // 300ms debounce

    setSearchTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchQuery, activeTab]);

  // Page change effect
  useEffect(() => {
    if (activeTab === 0) {
      fetchUsers();
    } else {
      fetchChallans();
    }
  }, [currentPage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: 1000,
        page: currentPage,
      };

      // Add search parameter if there's a search query
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const response = await api.get("/users", { params });
      setUsers(response.data.data.users);
      setTotalPages(response.data.data.pagination.pages);
      setTotalRecords(response.data.data.pagination.total);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChallans = async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: 1000,
        page: currentPage,
      };

      // Add search parameter if there's a search query
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const response = await api.get("/challans", { params });
      setChallans(response.data.data.challans);
      setTotalPages(response.data.data.pagination.pages);
      setTotalRecords(response.data.data.pagination.total);
    } catch (error) {
      console.error("Error fetching challans:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async (entityType: string, entityId: string) => {
    try {
      const response = await api.get(`/audit-logs/${entityType}/${entityId}`);
      setAuditLogs(response.data.data);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    }
  };

  const handleUserSelect = (user: User) => {
    console.log("Selected user:", user);
    console.log("User scholarship:", user.scholarship);
    setSelectedUser(user);
    setEditedUser({ ...user });
  };

  const handleChallanSelect = (challan: Challan) => {
    try {
      setSelectedChallan(challan);
      setEditedChallan({ ...challan });
    } catch (error) {
      console.error("Error selecting challan:", error);
      alert("Error selecting challan. Please try again.");
    }
  };

  const handleEdit = (type: "user" | "challan") => {
    if (type === "challan" && editedChallan) {
      if (editedChallan.paid) {
        if (!editedChallan.txnId || !editedChallan.branchCode || !editedChallan.txnDate) {
          alert("If payment status is paid, Transaction ID, Branch Code, and Transaction Date must be filled.");
          return;
        }
      } else {
        if (editedChallan.txnId || editedChallan.branchCode || editedChallan.txnDate) {
          alert("If payment status is unpaid, Transaction ID, Branch Code, and Transaction Date must be empty.");
          return;
        }
      }
    }
    setPendingAction(type);
    setWarningModalOpen(true);
  };

  const confirmEdit = async () => {
    if (!changeReason.trim()) {
      alert("Please provide a reason for this change");
      return;
    }

    try {
      setLoading(true);

      if (pendingAction === "user" && editedUser && selectedUser) {
        const { scholarship, ...userData } = editedUser;
        const updatePayload: any = {
          ...userData,
          reason: changeReason,
        };

        // Only include scholarship data if user has existing scholarship (editing existing one)
        if (scholarship && selectedUser.scholarship) {
          updatePayload.scholarship = scholarship;
        }

        await api.put(`/users/${selectedUser._id}`, updatePayload);
        await fetchUsers();
        setSelectedUser(null);
        setEditedUser(null);
      } else if (
        pendingAction === "challan" &&
        editedChallan &&
        selectedChallan
      ) {
        await api.put(`/challans/${selectedChallan._id}`, {
          ...editedChallan,
          reason: changeReason,
        });
        await fetchChallans();
        setSelectedChallan(null);
        setEditedChallan(null);
      }

      setWarningModalOpen(false);
      setChangeReason("");
      setPendingAction(null);
    } catch (error) {
      console.error("Error updating record:", error);
      alert("Failed to update record");
    } finally {
      setLoading(false);
    }
  };

  const handleViewAuditLog = (
    entityType: "user" | "challan",
    entityId: string
  ) => {
    fetchAuditLogs(entityType, entityId);
    setAuditLogModalOpen(true);
  };

  const handleDeleteUser = () => {
    setDeleteModalOpen(true);
  };

  const handleGeneratePDF = () => {
    setPdfModalOpen(true);
  };

  const confirmGeneratePDF = async () => {
    if (!adminApiKey.trim()) {
      alert("Please provide the admin API key");
      return;
    }

    if (!selectedUser) {
      alert("No user selected");
      return;
    }

    try {
      setPdfGenerating(true);

      const response = await generateUserPDF(
        selectedUser._id,
        adminApiKey,
        {
          fullName: selectedUser.fullName,
          rollNumber: selectedUser.rollNumber,
          email: selectedUser.email,
        }
      );
      const data = response.data;

      // Both existing and new challans now return JSON with fileUrl
      if (data.status === "success" && data.data && data.data.fileUrl) {
        // Extract path from fileUrl starting at /uploads
        const fileUrl = data.data.fileUrl;
        const uploadPath = fileUrl.substring(fileUrl.indexOf("/uploads"));
        window.open(`${USER_BASE_URL}${uploadPath}`, "_blank");

        // Show appropriate success message
        if (data.message === "Existing challan found") {
          alert(`Existing challan opened: ${data.data.challanNumber}`);
        } else if (data.message === "Challan generated successfully") {
          alert(`New challan generated and opened: ${data.data.challanNumber}`);
        } else {
          alert(`Challan opened: ${data.data.challanNumber}`);
        }
      } else {
        throw new Error("Invalid response format from challan API");
      }

      setPdfModalOpen(false);
      setAdminApiKey("");
    } catch (error: any) {
      console.error("Error generating challan:", error);
      alert(
        `Failed to generate challan: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setPdfGenerating(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!deleteReason.trim()) {
      alert("Please provide a reason for deletion");
      return;
    }

    if (!selectedUser) {
      alert("No user selected");
      return;
    }

    try {
      setLoading(true);

      await api.delete(`/users/${selectedUser._id}`, {
        data: { reason: deleteReason },
      });

      // Refresh the users list
      await fetchUsers();

      // Clear selected user and close modal
      setSelectedUser(null);
      setEditedUser(null);
      setDeleteModalOpen(false);
      setDeleteReason("");

      alert("User and all related data deleted successfully");
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setSelectedUser(null);
    setSelectedChallan(null);
    setEditedUser(null);
    setEditedChallan(null);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Backend now handles all the filtering, so we use results directly
  const filteredUsers = users;
  const filteredChallans = challans;

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" gutterBottom>
        Edit Records
      </Typography>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Warning:</strong> Changes made here will immediately reflect in
        the database. All modifications are logged for audit purposes.
      </Alert>

      <Paper sx={{ width: "100%" }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
        >
          <Tab icon={<PersonIcon />} label="Edit Users" />
          <Tab icon={<ReceiptIcon />} label="Edit Challans" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <TextField
            label="Search"
            placeholder={
              activeTab === 0
                ? "Search all users in database (name, email, roll number)..."
                : "Search all challans in database (challan ID, student name)..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
              endAdornment:
                loading && searchQuery ? <CircularProgress size={20} /> : null,
            }}
            helperText="Searches entire database - shows 1000 records per page"
          />

          <Box display="flex" gap={3}>
            {/* Left Panel - List */}
            <Paper sx={{ width: "50%", height: 500, overflow: "auto", p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {activeTab === 0
                  ? "Select User to Edit"
                  : "Select Challan to Edit"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {activeTab === 0
                  ? `Showing ${
                      filteredUsers.length
                    } of ${totalRecords.toLocaleString()} users${
                      searchQuery ? ` matching "${searchQuery}"` : ""
                    } (Page ${currentPage} of ${totalPages})`
                  : `Showing ${
                      filteredChallans.length
                    } of ${totalRecords.toLocaleString()} challans${
                      searchQuery ? ` matching "${searchQuery}"` : ""
                    } (Page ${currentPage} of ${totalPages})`}
              </Typography>

              {/* Pagination Controls */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1 || loading}
                  variant="outlined"
                  size="small"
                >
                  Previous
                </Button>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">Page</Typography>
                  <TextField
                    type="number"
                    value={currentPage}
                    onChange={(e) => {
                      const newPage = parseInt(e.target.value);
                      if (newPage >= 1 && newPage <= totalPages) {
                        handlePageChange(newPage);
                      }
                    }}
                    size="small"
                    sx={{ width: 70 }}
                    inputProps={{ min: 1, max: totalPages }}
                  />
                  <Typography variant="body2">of {totalPages}</Typography>
                </Box>

                <Button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages || loading}
                  variant="outlined"
                  size="small"
                >
                  Next
                </Button>
              </Box>

              <List>
                {loading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <Typography>Loading...</Typography>
                  </Box>
                ) : activeTab === 0 ? (
                  filteredUsers.map((user) => (
                    <ListItem
                      key={user._id}
                      onClick={() => handleUserSelect(user)}
                      sx={{
                        cursor: "pointer",
                        bgcolor:
                          selectedUser?._id === user._id
                            ? "action.selected"
                            : "inherit",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={user.fullName}
                        secondary={`${user.email} | ${user.rollNumber}`}
                      />
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Chip
                          label={user.isVerified ? "Verified" : "Unverified"}
                          color={user.isVerified ? "success" : "default"}
                          size="small"
                        />
                        {user.scholarship && (
                          <Chip
                            label={`Scholarship: ${
                              user.scholarship.status.charAt(0).toUpperCase() +
                              user.scholarship.status.slice(1)
                            }`}
                            color={
                              user.scholarship.status === "approved"
                                ? "success"
                                : user.scholarship.status === "rejected"
                                ? "error"
                                : "warning"
                            }
                            size="small"
                          />
                        )}
                      </Box>
                    </ListItem>
                  ))
                ) : (
                  filteredChallans.map((challan) => (
                    <ListItem
                      key={challan._id}
                      onClick={() => handleChallanSelect(challan)}
                      sx={{
                        cursor: "pointer",
                        bgcolor:
                          selectedChallan?._id === challan._id
                            ? "action.selected"
                            : "inherit",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <ListItemIcon>
                        <ReceiptIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Challan #${challan.challanId}`}
                        secondary={`${
                          challan.userId?.fullName || "Unknown User"
                        } | PKR ${challan.amount}`}
                      />
                      <Chip
                        label={challan.paid ? "Paid" : "Unpaid"}
                        color={challan.paid ? "success" : "error"}
                        size="small"
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>

            {/* Right Panel - Edit Form */}
            <Paper sx={{ width: "50%", height: 500, overflow: "auto", p: 2 }}>
              {activeTab === 0 && selectedUser && editedUser ? (
                <UserEditForm
                  user={editedUser}
                  onUserChange={setEditedUser}
                  onSave={() => handleEdit("user")}
                  onDelete={handleDeleteUser}
                  onGeneratePDF={handleGeneratePDF}
                  onViewAuditLog={() =>
                    handleViewAuditLog("user", selectedUser._id)
                  }
                  loading={loading}
                />
              ) : activeTab === 1 && selectedChallan && editedChallan ? (
                <ChallanEditForm
                  challan={editedChallan}
                  onChallanChange={setEditedChallan}
                  onSave={() => handleEdit("challan")}
                  onViewAuditLog={() =>
                    handleViewAuditLog("challan", selectedChallan._id)
                  }
                  loading={loading}
                />
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <Typography variant="body1" color="text.secondary">
                    Select a {activeTab === 0 ? "user" : "challan"} from the
                    left to start editing
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Paper>

      {/* Warning Modal */}
      <Dialog
        open={warningModalOpen}
        onClose={() => setWarningModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <WarningIcon color="warning" sx={{ mr: 1 }} />
            Confirm Changes
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to modify database records. This action is
            irreversible and will be logged.
          </Alert>
          <TextField
            label="Reason for Change (Required)"
            placeholder="Explain why this change is being made..."
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningModalOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmEdit}
            variant="contained"
            color="warning"
            disabled={!changeReason.trim() || loading}
          >
            Confirm Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <DeleteIcon color="error" sx={{ mr: 1 }} />
            Confirm User Deletion
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>DANGER:</strong> This will permanently delete the user and
            ALL related data including:
            <ul>
              <li>User account information</li>
              <li>All associated challans</li>
              <li>All associated scholarships</li>
            </ul>
            This action cannot be undone!
          </Alert>
          {selectedUser && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>User to be deleted:</strong>
              <br />
              Name: {selectedUser.fullName}
              <br />
              Email: {selectedUser.email}
              <br />
              Roll Number: {selectedUser.rollNumber}
            </Alert>
          )}
          <TextField
            label="Reason for Deletion (Required)"
            placeholder="Explain why this user is being deleted..."
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteUser}
            variant="contained"
            color="error"
            disabled={!deleteReason.trim() || loading}
            startIcon={<DeleteIcon />}
          >
            Delete User & All Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Generation Modal */}
      <Dialog
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PdfIcon color="info" sx={{ mr: 1 }} />
            Generate Challan PDF
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Challan Generation:</strong> This will generate or open the
            challan PDF for the selected user in a new tab. If a challan already
            exists, it will be opened. If not, a new one will be created and
            opened.
          </Alert>
          {selectedUser && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>User:</strong> {selectedUser.fullName}
              <br />
              <strong>Roll Number:</strong> {selectedUser.rollNumber}
              <br />
              <strong>Email:</strong> {selectedUser.email}
            </Alert>
          )}
          <TextField
            label="Admin API Key (Required)"
            placeholder="Enter your admin API key..."
            value={adminApiKey}
            onChange={(e) => setAdminApiKey(e.target.value)}
            fullWidth
            type="password"
            required
            helperText="This API key is required to authenticate the PDF generation request"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPdfModalOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmGeneratePDF}
            variant="contained"
            color="info"
            disabled={!adminApiKey.trim() || pdfGenerating}
            startIcon={
              pdfGenerating ? <CircularProgress size={20} /> : <PdfIcon />
            }
          >
            {pdfGenerating
              ? "Processing Challan..."
              : "Generate & Open Challan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Log Modal */}
      <Dialog
        open={auditLogModalOpen}
        onClose={() => setAuditLogModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <HistoryIcon sx={{ mr: 1 }} />
            Change History
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {auditLogs.map((log) => (
              <div key={log._id}>
                <ListItem>
                  <ListItemText
                    primary={`${log.action} by ${log.adminUser}`}
                    secondary={
                      <Box>
                        <Typography variant="body2">
                          {new Date(log.timestamp).toLocaleString()}
                        </Typography>
                        {log.reason && (
                          <Typography
                            variant="body2"
                            sx={{ fontStyle: "italic" }}
                          >
                            Reason: {log.reason}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1 }}>
                          {Object.entries(log.changes).map(
                            ([field, change]) => (
                              <Chip
                                key={field}
                                label={`${field}: ${change.old} → ${change.new}`}
                                size="small"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            )
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                <Divider />
              </div>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditLogModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

// User Edit Form Component
interface UserEditFormProps {
  user: User;
  onUserChange: (user: User) => void;
  onSave: () => void;
  onDelete: () => void;
  onGeneratePDF: () => void;
  onViewAuditLog: () => void;
  loading: boolean;
}

function UserEditForm({
  user,
  onUserChange,
  onSave,
  onDelete,
  onGeneratePDF,
  onViewAuditLog,
  loading,
}: UserEditFormProps) {
  const [newCourse, setNewCourse] = useState("");

  const handleChange = (field: keyof User, value: any) => {
    onUserChange({ ...user, [field]: value });
  };

  const handleScholarshipChange = (field: string, value: any) => {
    // Only allow changes if scholarship exists (editing existing one)
    if (!user.scholarship) {
      return; // Don't allow creating new scholarships
    }

    // Update existing scholarship
    const updatedScholarship = { ...user.scholarship, [field]: value };
    onUserChange({ ...user, scholarship: updatedScholarship });
  };

  const handleAddCourse = () => {
    // Ensure courses is always an array
    const userCourses = Array.isArray(user.courses) ? user.courses : [];

    if (
      newCourse.trim() &&
      !userCourses.includes(newCourse.trim()) &&
      AVAILABLE_COURSES.includes(newCourse.trim())
    ) {
      const updatedCourses = [...userCourses, newCourse.trim()];
      console.log(
        "Adding course:",
        newCourse.trim(),
        "Updated courses:",
        updatedCourses
      );
      handleChange("courses", updatedCourses);
      setNewCourse("");
    }
  };

  const handleRemoveCourse = (courseToRemove: string) => {
    // Ensure courses is always an array
    const userCourses = Array.isArray(user.courses) ? user.courses : [];

    const updatedCourses = userCourses.filter(
      (course) => course !== courseToRemove
    );
    console.log(
      "Removing course:",
      courseToRemove,
      "Updated courses:",
      updatedCourses
    );
    handleChange("courses", updatedCourses);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Edit User</Typography>
        <Tooltip title="View Change History">
          <IconButton onClick={onViewAuditLog}>
            <HistoryIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Stack spacing={2}>
        <TextField
          label="Full Name"
          value={user.fullName}
          onChange={(e) => handleChange("fullName", e.target.value)}
          fullWidth
        />
        <TextField
          label="Father Name"
          value={user.fatherName}
          onChange={(e) => handleChange("fatherName", e.target.value)}
          fullWidth
        />
        <Box display="flex" gap={2}>
          <TextField
            label="Email"
            type="email"
            value={user.email}
            onChange={(e) => handleChange("email", e.target.value)}
            fullWidth
          />
          <TextField
            label="Mobile"
            value={user.mobile}
            onChange={(e) => handleChange("mobile", e.target.value)}
            fullWidth
          />
        </Box>
        <Box display="flex" gap={2}>
          <TextField
            label="CNIC"
            value={user.cnic}
            onChange={(e) => handleChange("cnic", e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Gender</InputLabel>
            <Select
              value={user.gender}
              label="Gender"
              onChange={(e) => handleChange("gender", e.target.value)}
            >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box display="flex" gap={2}>
          <TextField
            label="Qualification"
            value={user.qualification}
            onChange={(e) => handleChange("qualification", e.target.value)}
            fullWidth
          />
          <TextField
            label="City"
            value={user.city}
            onChange={(e) => handleChange("city", e.target.value)}
            fullWidth
          />
        </Box>
        <TextField
          label="Address"
          value={user.permanentAddress}
          onChange={(e) => handleChange("permanentAddress", e.target.value)}
          fullWidth
          multiline
          rows={2}
        />

        {/* Courses Section */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Courses ({Array.isArray(user.courses) ? user.courses.length : 0})
          </Typography>
          {Array.isArray(user.courses) &&
            user.courses.some(
              (course) => !AVAILABLE_COURSES.includes(course)
            ) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Some courses are not in the current available courses list.
                These will be preserved but cannot be re-added if removed.
              </Alert>
            )}
          <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            {Array.isArray(user.courses) ? (
              user.courses.map((course, index) => (
                <Chip
                  key={index}
                  label={course}
                  onDelete={() => handleRemoveCourse(course)}
                  deleteIcon={<DeleteIcon />}
                  color={
                    AVAILABLE_COURSES.includes(course) ? "primary" : "default"
                  }
                  variant="outlined"
                  size="small"
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No courses assigned
              </Typography>
            )}
            {Array.isArray(user.courses) && user.courses.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No courses assigned
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Autocomplete
              options={AVAILABLE_COURSES.filter(
                (course) => !user.courses.includes(course)
              )}
              value={newCourse}
              onChange={(event, newValue) => setNewCourse(newValue || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add New Course"
                  placeholder="Select from available courses"
                  fullWidth
                  size="small"
                />
              )}
              freeSolo={false}
              autoComplete
              autoHighlight
            />
            <Button
              variant="outlined"
              onClick={handleAddCourse}
              disabled={
                !newCourse.trim() ||
                user.courses.includes(newCourse.trim()) ||
                !AVAILABLE_COURSES.includes(newCourse.trim())
              }
              startIcon={<AddIcon />}
              size="small"
            >
              Add
            </Button>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary">
          <strong>Available Courses:</strong> {AVAILABLE_COURSES.length}{" "}
          predefined courses available for selection.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={user.isVerified}
              onChange={(e) => handleChange("isVerified", e.target.checked)}
            />
          }
          label="Verified User"
        />

        {/* Scholarship Section */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Scholarship Information
          </Typography>

          {user.scholarship ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>
                  Scholarship found - you can edit the details below.
                </strong>
                <br />
                <strong>Applied:</strong>{" "}
                {new Date(user.scholarship.appliedAt).toLocaleDateString()}
              </Alert>

              <Stack spacing={2}>
                <TextField
                  label="Challan Number"
                  value={user.scholarship.challanNumber || ""}
                  onChange={(e) =>
                    handleScholarshipChange("challanNumber", e.target.value)
                  }
                  fullWidth
                  placeholder="Enter challan number for scholarship"
                />

                <FormControl fullWidth>
                  <InputLabel>Scholarship Status</InputLabel>
                  <Select
                    value={user.scholarship.status}
                    label="Scholarship Status"
                    onChange={(e) =>
                      handleScholarshipChange("status", e.target.value)
                    }
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Image Path"
                  value={user.scholarship.imagePath || ""}
                  onChange={(e) =>
                    handleScholarshipChange("imagePath", e.target.value)
                  }
                  fullWidth
                  placeholder="Path to scholarship documents/images"
                  helperText="Optional: Path to uploaded documents or images"
                />
              </Stack>
            </>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>No scholarship data available.</strong>
              <br />
              This user has not applied for a scholarship yet. Scholarships must
              be applied through the student portal.
            </Alert>
          )}
        </Box>

        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={loading}
            fullWidth
          >
            Save Changes
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<PdfIcon />}
            onClick={onGeneratePDF}
            disabled={loading}
            fullWidth
          >
            Generate Challan
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            disabled={loading}
            fullWidth
          >
            Delete User
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

// Challan Edit Form Component
interface ChallanEditFormProps {
  challan: Challan;
  onChallanChange: (challan: Challan) => void;
  onSave: () => void;
  onViewAuditLog: () => void;
  loading: boolean;
}

function ChallanEditForm({
  challan,
  onChallanChange,
  onSave,
  onViewAuditLog,
  loading,
}: ChallanEditFormProps) {
  const handleChange = (field: keyof Challan, value: any) => {
    if (field === "paid" && value === false) {
      onChallanChange({
        ...challan,
        paid: false,
        txnId: null,
        branchCode: null,
        txnDate: null,
      });
    } else {
      onChallanChange({ ...challan, [field]: value });
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Edit Challan</Typography>
        <Tooltip title="View Change History">
          <IconButton onClick={onViewAuditLog}>
            <HistoryIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Stack spacing={2}>
        <TextField
          label="User"
          value={
            challan.userId
              ? `${challan.userId.fullName} (${challan.userId.email})`
              : "Unknown User (User Deleted)"
          }
          fullWidth
          disabled
        />
        <Box display="flex" gap={2}>
          <TextField
            label="Challan ID"
            value={challan.challanId}
            onChange={(e) => handleChange("challanId", e.target.value)}
            fullWidth
          />
          <TextField
            label="Amount"
            type="number"
            value={challan.amount}
            onChange={(e) => handleChange("amount", parseInt(e.target.value))}
            fullWidth
          />
        </Box>
        <Box display="flex" gap={2}>
          <TextField
            label="Transaction ID"
            type="number"
            value={challan.txnId || ""}
            onChange={(e) =>
              handleChange(
                "txnId",
                e.target.value ? parseInt(e.target.value) : null
              )
            }
            fullWidth
          />
          <TextField
            label="Branch Code"
            type="number"
            value={challan.branchCode || ""}
            onChange={(e) =>
              handleChange(
                "branchCode",
                e.target.value ? parseInt(e.target.value) : null
              )
            }
            fullWidth
          />
        </Box>
        <TextField
          label="Transaction Date"
          type="date"
          value={challan.txnDate ? challan.txnDate.split("T")[0] : ""}
          onChange={(e) =>
            handleChange(
              "txnDate",
              e.target.value
                ? new Date(e.target.value).toISOString()
                : null
            )
          }
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={challan.paid}
              onChange={(e) => handleChange("paid", e.target.checked)}
            />
          }
          label="Payment Status"
        />
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={onSave}
          disabled={loading}
          fullWidth
        >
          Save Changes
        </Button>
      </Stack>
    </Box>
  );
}

export default EditRecords;
